import { ColumnDef, FilterFnOption } from '@tanstack/react-table';
import { useMemo } from 'react';
import { SeatingInfo } from 'src/components/common/SeatingInfo';
import { ListingNotesDisplay } from 'src/components/Listings/ListingDisplay/ListingNotesDisplay';
import {
  SaleMarketplaceIdDisplay,
  SaleMarketplaceIdDisplayProps,
} from 'src/components/Sales/SaleMarketplaceIdDisplay';
import { BaseCell } from 'src/components/TableV2/cells';
import {
  COLUMN_DEFAULT_SIZE_BASE,
  COLUMN_DEFAULT_SIZE_BASE_L,
  COLUMN_DEFAULT_SIZE_L,
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_DEFAULT_SIZE_XL,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useSellerAccountContext } from 'src/contexts/SellerAccountContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { vars } from 'src/core/themes';
import { Stack, TooltipPopover } from 'src/core/ui';
import { CopyToClipboardV2 } from 'src/core/ui/CopyToClipboard/CopyToClipboardV2';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useConsigneeAccountDisplayName } from 'src/hooks/api/useConsigneeAccountDisplayName';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { getUserDisplayName, useGetUserInfos } from 'src/hooks/userGetUserInfo';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FormatOption } from 'src/modals/EditTableColumns';
import { LaunchUploadBarcodes } from 'src/modals/UploadBarcodes/LaunchUploadBarcodes';
import { IconsFill, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { TableShimmeringDiv } from 'src/tables/Table';
import { InHandCell } from 'src/tables/Table/TableCells';
import { createDateColumn } from 'src/tables/TableV2/factories/dateColumnFactory';
import { createLinkColumn } from 'src/tables/TableV2/factories/linkColumnFactory';
import { createMoneyColumn } from 'src/tables/TableV2/factories/moneyColumnFactory';
import { createNumberColumn } from 'src/tables/TableV2/factories/numberColumnFactory';
import { createPercentColumn } from 'src/tables/TableV2/factories/percentColumnFactory';
import { createSeatingColumn } from 'src/tables/TableV2/factories/seatingColumnFactory';
import { createTextColumn } from 'src/tables/TableV2/factories/textColumnFactory';
import { ContentId } from 'src/utils/constants/contentId';
import {
  MARKETPLACE_SALE_PAYMENT_STATE_TO_CID,
  SALE_PAYMENT_STATE_TO_CID,
  SALE_TABLE_COLUMN_ID_TO_CONTENT_ID,
  TICKET_TYPE_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { getPurchaseOrderRelativeUrl } from 'src/utils/purchaseUtils';
import {
  getSaleStatusDisplayContentId,
  getSaleStatusIcon,
} from 'src/utils/saleUtils';
import {
  filterSeatingSectionFn,
  filterSeatRangeFn,
  filterSeatTraitsFn,
  filterStringMultiSelect,
  safeSortDateTime,
  sortGeneric,
  sortSeatingSeatsForTable,
} from 'src/utils/tableUtils';
import { getTicketTypeIcon } from 'src/utils/ticketTypeUtils';
import { SectionType } from 'src/utils/types/sectionType';
import {
  Feature,
  ListingNote,
  MarketplaceFulfillmentState,
  PointOfSaleSaleStatus,
  PosClientConfig,
  SaleActionType,
  SaleClient,
  TicketBarcode,
  TicketType,
} from 'src/WebApiController';

import { SalesTableColumnId } from '../../../utils/columns/sales/salesColumnUtils.types';
import {
  SaleWithEvent,
  SHIMMERING_DIV_HEIGHT_SINGLE_SALE,
} from '../SalesTable.type';
import { SalesActionsCell } from './SalesActionsCell';

const COST_COLUMN_MIN_SIZE = 75;
const COST_COLUMN_MAX_SIZE = 125;

const SECTION_TYPE = SectionType.Sales;

export const SALE_TABLE_COLUMNS_CONFIG_V2: Partial<
  Record<SalesTableColumnId, ColumnDef<SaleWithEvent | null>>
> = {
  [SalesTableColumnId.Seating]: createSeatingColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.Seating,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Seating],
    accessor: (data) =>
      data
        ? {
            seating: data.sale.seating,
            listingNotes: data.sale.seatTraits,
            isSeatSaver: data.sale.isSeatSaver,
            status: data.sale.status,
          }
        : null,
    hasMarketplaceProblemAccessor: (data) =>
      data?.sale?.hasMarketplaceProblem ?? false,
    size: COLUMN_DEFAULT_SIZE_XL,
    enableColumnFilter: true,
    filterFn: filterSeatingSectionFn as FilterFnOption<SaleWithEvent | null>,
  }),
  [SalesTableColumnId.SeatTraits]: {
    id: SalesTableColumnId.SeatTraits,
    header: ({ column: { id } }) => (
      <Content
        id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableColumnId]}
      />
    ),
    enableColumnFilter: true,
    filterFn: filterSeatTraitsFn as FilterFnOption<SaleWithEvent | null>,
    meta: {
      uniqueValueVariant: 'seatTraits',
    },
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.sale?.seatTraits,
    cell: function Cell({ getValue, row: { original } }) {
      const seatTraits = getValue<ListingNote[] | null | undefined>();

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return (
        <BaseCell>
          <ListingNotesDisplay seatTraits={seatTraits} />
        </BaseCell>
      );
    },
  },
  [SalesTableColumnId.QuantitySold]: createNumberColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.QuantitySold,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.QuantitySold],
    accessor: (data) => (data ? data.sale.qtySold || 0 : null),
    size: COLUMN_DEFAULT_SIZE_BASE,
    withFooter: true,
  }),
  [SalesTableColumnId.OrderDate]: createDateColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.OrderDate,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.OrderDate],
    accessor: (data) => data?.sale?.saleDate,
    size: COLUMN_DEFAULT_SIZE_L,
    defaultPrecision: FormatOption.DateTime_MonthDD_Time,
  }),
  [SalesTableColumnId.InHandDate]: {
    id: SalesTableColumnId.InHandDate,
    header: () => <TableHeaderCellDiv align="left">IH</TableHeaderCellDiv>,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_BASE_L,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => data?.sale.inHandAt,
    cell: ({ getValue, row: { original } }) => {
      const value = getValue<string | null>();
      if (!original?.sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }
      return (
        <BaseCell>
          <InHandCell
            date={value ? new Date(value) : null}
            needsToFulfill={Boolean(
              original?.sale.status &&
                ![
                  PointOfSaleSaleStatus.ProcessingFulfillment,
                  PointOfSaleSaleStatus.Fulfilled,
                  PointOfSaleSaleStatus.CancelledHold,
                  PointOfSaleSaleStatus.Rejected,
                  PointOfSaleSaleStatus.PendingRejection,
                ].includes(original?.sale.status)
            )}
            handleFulfillmentState
          />
        </BaseCell>
      );
    },
    sortingFn: safeSortDateTime<SaleWithEvent>,
  },
  [SalesTableColumnId.Status]: {
    id: SalesTableColumnId.Status,
    header: ({ column: { id } }) => (
      <Content
        id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableColumnId]}
      />
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => (data ? data.sale.status : null),
    cell: function Cell({ getValue, column: { id: columnId }, row, table }) {
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );

      const sale = row.original?.sale;
      const value = getValue() as PointOfSaleSaleStatus;

      const statusContent = useContent(
        getSaleStatusDisplayContentId(value, sale?.mkpContrState, sale?.mkp)
      );

      if (value == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const SaleStatusIcon = getSaleStatusIcon(
        value,
        sale?.mkpContrState,
        sale?.mkp
      );

      return (
        <BaseCell>
          <Stack direction="row" gap="m" alignItems="center">
            {userDefinedPrecision === FormatOption.EnumDisplay_Icon ? (
              <SaleStatusIcon fill={'transparent'} title={statusContent} />
            ) : (
              <div>{statusContent}</div>
            )}

            {
              // if the Sale is fulfilled in both POS and Marketplace, but the version is mismatched, show the warning
              value === PointOfSaleSaleStatus.Fulfilled &&
              sale?.isFulfillVerMis ? (
                <Stack>
                  <TooltipPopover
                    variant="link"
                    triggerContent={
                      <WarningOutlineIcon
                        fill={IconsFill.textWarning}
                        withHoverEffect
                      />
                    }
                  >
                    <WarningMessage
                      message={
                        <Content id={ContentId.VersionMismatchExplanation} />
                      }
                    />
                  </TooltipPopover>
                </Stack>
              ) : value !== PointOfSaleSaleStatus.Fulfilled &&
                sale?.mkpFulfState === MarketplaceFulfillmentState.Fulfilled ? (
                <Stack>
                  <TooltipPopover
                    variant="link"
                    triggerContent={
                      <WarningOutlineIcon
                        fill={IconsFill.textWarning}
                        withHoverEffect
                      />
                    }
                  >
                    <WarningMessage
                      message={
                        <Content id={ContentId.SaleIsFulfilledOnMarketplace} />
                      }
                    />
                  </TooltipPopover>
                </Stack>
              ) : undefined
            }
          </Stack>
        </BaseCell>
      );
    },
  },
  [SalesTableColumnId.TicketType]: {
    id: SalesTableColumnId.TicketType,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) => (data ? data.sale.ticketType : null),
    cell: function Cell({
      getValue,
      column: { id: columnId },
      table,
      row: { original },
    }) {
      const hasSaleEditBarcodeFeature = useUserHasFeature(
        Feature.SaleEditBarcode
      );
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? SECTION_TYPE
      );
      const value = getValue() as TicketType;
      if (value == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (
        hasSaleEditBarcodeFeature &&
        value === TicketType.Barcode &&
        original?.sale
      ) {
        const isEditable = original?.sale.actions.some(
          (action) =>
            action === SaleActionType.Fulfill ||
            action === SaleActionType.Refulfill
        );

        return (
          <BaseCell>
            <LaunchUploadBarcodes
              isReadOnly={!isEditable}
              onUploadBarcodes={(
                clientConfig: PosClientConfig,
                saleId: number,
                barcodes: TicketBarcode[]
              ) =>
                new SaleClient(clientConfig).updateBarcodesForSale(
                  saleId,
                  barcodes
                )
              }
              entityId={original?.sale.id}
              idOnMkp={original?.sale.idOnMkp}
              barcodeIconOnlyMode={true}
              iconOnlyMode={true}
              iconStyle={{ padding: 0 }}
              cancelTo={undefined}
              variant={undefined}
              action={SaleActionType.Refulfill}
              loadingContentId={FormatContentId.LoadingSaleId}
              errorContentId={FormatContentId.CouldNotFindSaleId}
              searchingContentId={FormatContentId.SearchingForSaleId}
              isSaleNoFulfill={original?.sale.isNoFulfill}
            />
          </BaseCell>
        );
      }
      const ticketTypeIcon = getTicketTypeIcon(value, IconsFill.currentColor);
      return (
        <BaseCell align="left" title={value}>
          {userDefinedPrecision === FormatOption.EnumDisplay_Icon ? (
            <span style={{ color: vars.color.textStrong }}>
              {ticketTypeIcon}
            </span>
          ) : (
            <Content id={TICKET_TYPE_TO_CID[value]} />
          )}
        </BaseCell>
      );
    },
  },
  [SalesTableColumnId.Proceeds]: createMoneyColumn({
    id: SalesTableColumnId.Proceeds,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Proceeds],
    accessor: (data) => data?.sale?.ttlNetProcs,
    withFooter: true,
  }),
  [SalesTableColumnId.SoldTicketCost]: createMoneyColumn({
    id: SalesTableColumnId.SoldTicketCost,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.SoldTicketCost],
    accessor: (data) => data?.sale?.soldTktCost,
    withThresholds: true,
    withFooter: true,
  }),
  [SalesTableColumnId.PandL]: createMoneyColumn({
    id: SalesTableColumnId.PandL,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.PandL],
    accessor: (data) => data?.sale?.pandL,
    withThresholds: true,
    withFooter: true,
  }),
  [SalesTableColumnId.Margin]: createPercentColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.Margin,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Margin],
    accessor: (data) => data?.sale?.margin,
    withThresholds: true,
    withFooter: true,
  }),
  [SalesTableColumnId.Charges]: createMoneyColumn({
    id: SalesTableColumnId.Charges,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Charges],
    accessor: (data) => data?.sale?.charges,
    withFooter: true,
  }),
  [SalesTableColumnId.Credits]: createMoneyColumn({
    id: SalesTableColumnId.Credits,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Credits],
    accessor: (data) => data?.sale?.credits,
    withFooter: true,
  }),
  [SalesTableColumnId.Sale]: {
    id: SalesTableColumnId.Sale,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="right">
        <Content
          id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableColumnId]}
        />
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_XL,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) =>
      !data
        ? null
        : {
            marketplaceSaleId: data.sale.idOnMkp,
            saleId: data.sale.id,
            isExternal: Boolean(data.sale.idOnMkp),
            marketplace: data.sale.mkp,
          },
    cell: ({ getValue }) => {
      const value = getValue();
      if (value == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      const props = value as SaleMarketplaceIdDisplayProps;
      return (
        <TableCellDiv align="right" style={{ height: '100%' }}>
          <Stack
            gap="s"
            alignItems="center"
            justifyContent="end"
            width="full"
            height="full"
            style={{ wordBreak: 'break-all' }}
          >
            <CopyToClipboardV2
              textToCopy={props.marketplaceSaleId || props.saleId}
            >
              <SaleMarketplaceIdDisplay {...props} />
            </CopyToClipboardV2>
          </Stack>
        </TableCellDiv>
      );
    },
    // sort by market, then order
    sortingFn: (rowA, rowB, colId) => {
      const a = rowA.getValue(colId) as SaleMarketplaceIdDisplayProps;
      const b = rowB.getValue(colId) as SaleMarketplaceIdDisplayProps;
      if (!a) return b ? -1 : 1;
      if (!b) return 1;

      if (a.marketplace !== b.marketplace) {
        return a.marketplace < b.marketplace ? -1 : 1;
      }

      // Same marketplace, sort by sale id
      return (a.marketplaceSaleId || '') < (b.marketplaceSaleId || '') ? -1 : 1;
    },
  },
  [SalesTableColumnId.Actions]: {
    id: SalesTableColumnId.Actions,
    enableSorting: false,
    enableResizing: true,
    header: function Cell() {
      return null;
    },
    enableColumnFilter: false,
    // size: COLUMN_DEFAULT_SIZE_LG,
    // minSize: COLUMN_DEFAULT_SIZE_LG,
    // maxSize: COLUMN_MAX_SIZE,
    cell: ({ row: { original }, table }) => {
      const linkOnly =
        table.options.meta?.formatOptionOverrides?.[
          SalesTableColumnId.Actions
        ] === FormatOption.Actions_LinkOnly;

      return !original ? (
        <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
      ) : (
        <BaseCell>
          <SalesActionsCell
            linkOnly={linkOnly}
            saleId={original.sale.id}
            marketplaceSaleId={original.sale.idOnMkp}
            marketplace={original.sale.mkp}
            saleStatus={original.sale.status}
            actions={original.sale.actions}
            ticketType={original.sale.ticketType}
            isNoFulfill={original.sale.isNoFulfill}
          />
        </BaseCell>
      );
    },
  },
  [SalesTableColumnId.FulfillmentDate]: createDateColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.FulfillmentDate,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.FulfillmentDate],
    accessor: (data) => data?.sale?.fulfillDate,
    size: COLUMN_DEFAULT_SIZE_L,
    defaultPrecision: FormatOption.DateTime_MonthDD_Time,
  }),
  [SalesTableColumnId.AutoFulfill]: createTextColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.AutoFulfill,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.AutoFulfill],
    accessor: (data) => data?.sale?.isNoFulfill,
    size: COLUMN_DEFAULT_SIZE_LG,
    customCellRenderer: (props: any) => (
      <BaseCell>
        <Content id={props.value ? ContentId.No : ContentId.Yes} />
      </BaseCell>
    ),
  }),
  [SalesTableColumnId.PaymentState]: createTextColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.PaymentState,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.PaymentState],
    accessor: (data) => {
      const mkpPmtState = data?.sale?.mkpPmtState;
      const pmtState = data?.sale?.pmtState;

      if (mkpPmtState != null) {
        return MARKETPLACE_SALE_PAYMENT_STATE_TO_CID[mkpPmtState];
      }
      if (pmtState != null) {
        return SALE_PAYMENT_STATE_TO_CID[pmtState];
      }
      return null;
    },
    size: COLUMN_DEFAULT_SIZE_LG,
    customCellRenderer: (props: any) => (
      <BaseCell>
        {props.value != null && <Content id={props.value as string} />}
      </BaseCell>
    ),
  }),
  [SalesTableColumnId.PaymentReceived]: createTextColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.PaymentReceived,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.PaymentReceived],
    accessor: (data) => data?.sale?.pmtRcv,
    size: COLUMN_DEFAULT_SIZE_LG,
    customCellRenderer: (props: any) => (
      <BaseCell>
        <Content id={props.value ? ContentId.Yes : ContentId.No} />
      </BaseCell>
    ),
  }),
  [SalesTableColumnId.PaymentReferenceId]:
    createTextColumn<SaleWithEvent | null>({
      id: SalesTableColumnId.PaymentReferenceId,
      headerContentId:
        SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[
          SalesTableColumnId.PaymentReferenceId
        ],
      accessor: (data) => data?.sale?.pmtRefId,
      size: COLUMN_DEFAULT_SIZE_LG,
    }),
  [SalesTableColumnId.PurchaseCreatedBy]: {
    id: SalesTableColumnId.PurchaseCreatedBy,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <Content
        id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableColumnId]}
      />
    ),
    accessorFn: (data) => {
      return data?.sale?.owners?.poInsertBys;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const { allActiveUserInfos } = useSellerAccountContext();

      const sellerUserIds = getValue<string[] | null>();

      const displayNamesFormatted = useMemo(
        () =>
          sellerUserIds
            ? [...new Set(sellerUserIds)]
                .map(
                  (sellerUserId) =>
                    allActiveUserInfos?.[sellerUserId]?.displayName
                )
                .filter((name) => name != null)
                .join(', ')
            : null,
        [allActiveUserInfos, sellerUserIds]
      );

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return <BaseCell>{displayNamesFormatted}</BaseCell>;
    },
  },
  [SalesTableColumnId.PurchasedBy]: {
    id: SalesTableColumnId.PurchasedBy,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <Content
        id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableColumnId]}
      />
    ),
    accessorFn: (data) => {
      return data?.sale?.owners?.buyerIds;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const { allActiveUserInfos } = useSellerAccountContext();

      const sellerUserIds = getValue<string[] | null>();

      const displayNamesFormatted = useMemo(
        () =>
          sellerUserIds
            ? [...new Set(sellerUserIds)]
                .map(
                  (sellerUserId) =>
                    allActiveUserInfos?.[sellerUserId]?.displayName
                )
                .filter((name) => name != null)
                .join(', ')
            : null,
        [allActiveUserInfos, sellerUserIds]
      );

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return <BaseCell>{displayNamesFormatted}</BaseCell>;
    },
  },
  [SalesTableColumnId.PurchaseDate]: createDateColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.PurchaseDate,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.PurchaseDate],
    accessor: (data) => data?.sale?.poDate,
    size: COLUMN_DEFAULT_SIZE_L,
    defaultPrecision: FormatOption.DateTime_MonthDD_Time,
  }),
  [SalesTableColumnId.VendorOrderId]: createLinkColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.VendorOrderId,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.VendorOrderId],
    linkAccessor: (data) =>
      data?.sale?.poId ? getPurchaseOrderRelativeUrl(data?.sale?.poId) : null,
    textAccessor: (data) => String(data?.sale?.poVendOrdId),
    size: COLUMN_DEFAULT_SIZE_BASE_L,
    sectionType: SectionType.RecentSalesTable,
  }),
  [SalesTableColumnId.VendorAccountEmail]:
    createTextColumn<SaleWithEvent | null>({
      id: SalesTableColumnId.VendorAccountEmail,
      headerContentId:
        SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[
          SalesTableColumnId.VendorAccountEmail
        ],
      accessor: (data) => data?.sale?.poVendAccEmail,
      size: COLUMN_DEFAULT_SIZE_LG,
    }),
  [SalesTableColumnId.OriginatedBy]: {
    id: SalesTableColumnId.OriginatedBy,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <Content
        id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableColumnId]}
      />
    ),
    accessorFn: (data) => {
      return data?.sale?.origBy;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const deactivatedText = useContent(ContentId.Deactivated);
      const insertByUserId = getValue() as string;
      const getUserInfosQuery = useGetUserInfos([insertByUserId]);

      const displayUserName = useMemo(() => {
        const user = insertByUserId
          ? getUserInfosQuery.data?.[insertByUserId]
          : undefined;

        return user ? getUserDisplayName(user, deactivatedText) : '';
      }, [deactivatedText, getUserInfosQuery.data, insertByUserId]);

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return <BaseCell>{displayUserName}</BaseCell>;
    },
  },
  [SalesTableColumnId.Row]: {
    id: SalesTableColumnId.Row,
    enableColumnFilter: true,
    filterFn: filterStringMultiSelect as FilterFnOption<SaleWithEvent | null>,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <Content
        id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableColumnId]}
      />
    ),
    accessorFn: (data) => {
      return data?.sale?.seating?.row;
    },
    cell: function Cell({ getValue, row: { original } }) {
      const value = getValue<string>();

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }
      if (!original.sale) return null;

      return <BaseCell>{value}</BaseCell>;
    },
    sortingFn: sortGeneric,
  },
  [SalesTableColumnId.Seats]: {
    id: SalesTableColumnId.Seats,
    enableColumnFilter: true,
    filterFn: filterSeatRangeFn as FilterFnOption<SaleWithEvent | null>,
    meta: {
      columnFilterVariant: 'seatRange',
    },
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: ({ column: { id } }) => (
      <Content
        id={SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[id as SalesTableColumnId]}
      />
    ),
    accessorFn: (data) => {
      return data?.sale?.seating;
    },
    cell: function Cell({ row: { original } }) {
      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }
      if (!original.sale) return null;

      const { seatFr, seatTo } = original.sale.seating;

      return (
        <SeatingInfo
          section={''}
          row={null}
          seatFr={seatFr}
          seatTo={seatTo}
          showSeatPrefix={false}
          justifyIconContent="spaceBetween"
        />
      );
    },
    sortingFn: sortSeatingSeatsForTable,
  },
  [SalesTableColumnId.TotalPaymentsAmount]: createMoneyColumn({
    id: SalesTableColumnId.TotalPaymentsAmount,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[
        SalesTableColumnId.TotalPaymentsAmount
      ],
    accessor: (data) => data?.sale?.totalPmt,
    withFooter: true,
  }),
  [SalesTableColumnId.TotalConvertedPaymentsAmount]: createMoneyColumn({
    id: SalesTableColumnId.TotalConvertedPaymentsAmount,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[
        SalesTableColumnId.TotalConvertedPaymentsAmount
      ],
    accessor: (data) => data?.sale?.totalConvertedPmt,
    withFooter: true,
  }),
  [SalesTableColumnId.WastedQuantity]: createTextColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.WastedQuantity,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.WastedQuantity],
    accessor: (data) => data?.sale?.qtyWasted,
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    withFooter: true,
    align: 'right',
  }),

  [SalesTableColumnId.InternalNotes]: createTextColumn<SaleWithEvent | null>({
    id: SalesTableColumnId.InternalNotes,
    headerContentId:
      SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.InternalNotes],
    accessor: (data) => data?.sale?.internalNotesPreview,
  }),

  [SalesTableColumnId.ConsigneeSellerAccountId]: {
    id: SalesTableColumnId.ConsigneeSellerAccountId,
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    header: () => (
      <Content
        id={
          SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[
            SalesTableColumnId.ConsigneeSellerAccountId
          ]
        }
      />
    ),
    accessorFn: (data) => data?.sale?.accId,
    cell: function Cell({ getValue, row: { original } }) {
      const { accountName } =
        useConsigneeAccountDisplayName(getValue<string | null>());

      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      return <BaseCell>{accountName}</BaseCell>;
    },
  },
};
